import { exec } from 'child_process';
import { promisify } from 'util';
import { directoryExists } from './fs.js';
import path from 'path';

const execAsync = promisify(exec);

/**
 * Custom error class for git operations
 */
export class GitError extends Error {
  constructor(
    message: string,
    public readonly command: string,
    public readonly cwd?: string,
    public readonly cause?: Error
  ) {
    super(message);
    this.name = 'GitError';
  }
}

/**
 * Execute a git command with proper error handling
 */
export async function git(command: string, cwd?: string): Promise<string> {
  const options = cwd ? { cwd, maxBuffer: 10 * 1024 * 1024 } : { maxBuffer: 10 * 1024 * 1024 };
  try {
    const { stdout } = await execAsync(`git ${command}`, options);
    return stdout.trim();
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    // Extract stderr if available
    const stderr = (error as { stderr?: string })?.stderr || '';
    throw new GitError(
      `Git command failed: git ${command}${stderr ? ` - ${stderr.trim()}` : ''}`,
      command,
      cwd,
      err
    );
  }
}

/**
 * Check if directory is a git repository
 */
export async function isGitRepo(cwd?: string): Promise<boolean> {
  const gitDir = path.join(cwd || process.cwd(), '.git');
  return directoryExists(gitDir);
}

/**
 * Get current branch name
 */
export async function getCurrentBranch(cwd?: string): Promise<string | null> {
  try {
    return await git('branch --show-current', cwd);
  } catch {
    return null;
  }
}

/**
 * Check if working directory is clean
 */
export async function isClean(cwd?: string): Promise<boolean> {
  try {
    const status = await git('status --porcelain', cwd);
    return status === '';
  } catch {
    return false;
  }
}

/**
 * Get list of changed files
 */
export async function getChangedFiles(cwd?: string): Promise<string[]> {
  try {
    const status = await git('status --porcelain', cwd);
    if (!status) return [];

    return status.split('\n')
      .map(line => line.substring(3).trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Get commit count between two refs
 */
export async function getCommitCount(
  from: string,
  to: string,
  cwd?: string
): Promise<number> {
  try {
    const result = await git(`rev-list --count ${from}..${to}`, cwd);
    return parseInt(result, 10);
  } catch {
    return 0;
  }
}

/**
 * Get latest tag
 */
export async function getLatestTag(cwd?: string): Promise<string | null> {
  try {
    return await git('describe --tags --abbrev=0', cwd);
  } catch {
    return null;
  }
}

/**
 * Check if branch exists
 */
export async function branchExists(branch: string, cwd?: string): Promise<boolean> {
  try {
    await git(`rev-parse --verify ${branch}`, cwd);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get list of all branches
 */
export async function getBranches(cwd?: string): Promise<string[]> {
  try {
    const result = await git('branch -a', cwd);
    return result.split('\n')
      .map(line => line.replace(/^\*?\s+/, '').trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Get file content from a specific branch
 */
export async function getFileFromBranch(
  branch: string,
  filePath: string,
  cwd?: string
): Promise<string | null> {
  try {
    return await git(`show ${branch}:${filePath}`, cwd);
  } catch {
    return null;
  }
}

/**
 * Get diff between branches for a specific file
 */
export async function getDiff(
  fromBranch: string,
  toBranch: string,
  filePath?: string,
  cwd?: string
): Promise<string> {
  try {
    const pathArg = filePath ? ` -- ${filePath}` : '';
    return await git(`diff ${fromBranch}...${toBranch}${pathArg}`, cwd);
  } catch {
    return '';
  }
}

/**
 * Get remote URL
 */
export async function getRemoteUrl(remote: string = 'origin', cwd?: string): Promise<string | null> {
  try {
    return await git(`remote get-url ${remote}`, cwd);
  } catch {
    return null;
  }
}

/**
 * Get list of staged files
 */
export async function getStagedFiles(cwd?: string): Promise<string[]> {
  try {
    const status = await git('diff --cached --name-only', cwd);
    if (!status) return [];

    return status.split('\n').filter(Boolean);
  } catch {
    return [];
  }
}
